import React, { useState } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";

import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Link from "@material-ui/core/Link";
import { makeStyles } from "@material-ui/core/styles";
import InputAdornment from "@material-ui/core/InputAdornment";
import EmailIcon from "@material-ui/icons/Email";
import ChatRoundedIcon from "@material-ui/icons/ChatRounded";

import { i18n } from "../../translate/i18n";
import { openApi } from "../../services/api";

const useStyles = makeStyles(() => ({
	root: {
		minHeight: "100dvh",
		fallbacks: [{ minHeight: "100vh" }], // older WebViews without dvh
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		background: "#f5f5f7",
		fontFamily: "'Roboto',system-ui,-apple-system,'Segoe UI','Helvetica Neue',sans-serif",
		padding: 24,
	},
	card: {
		background: "#fff",
		borderRadius: 16,
		padding: 32,
		width: "100%",
		maxWidth: 420,
		boxShadow: "0 1px 2px rgba(15,15,15,0.04), 0 6px 18px rgba(15,15,15,0.04)",
	},
	brand: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		marginBottom: 24,
	},
	brandChip: {
		width: 36,
		height: 36,
		borderRadius: 10,
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		background: "linear-gradient(135deg, #2ee37a 0%, #25d366 55%, #128c7e 100%)",
		color: "#fff",
		"& svg": { fontSize: 20 },
	},
	brandText: {
		fontWeight: 600,
		fontSize: 20,
		color: "#0B0D0C",
		letterSpacing: -0.3,
	},
	title: {
		fontSize: 22,
		fontWeight: 600,
		margin: "0 0 8px",
		color: "#0B0D0C",
	},
	subtitle: {
		fontSize: 14,
		color: "#71717a",
		margin: "0 0 24px",
		lineHeight: 1.5,
	},
	submitBtn: {
		marginTop: 16,
		padding: "12px 16px",
		background: "#0B0D0C",
		color: "#fff",
		fontWeight: 600,
		borderRadius: 999,
		textTransform: "none",
		"&:hover": { background: "#000" },
		"&:disabled": { background: "#a1a1aa", color: "#fff" },
	},
	successBox: {
		background: "#ecfdf5",
		border: "1px solid #a7f3d0",
		borderRadius: 10,
		padding: 14,
		marginTop: 16,
		fontSize: 14,
		color: "#065f46",
		lineHeight: 1.5,
	},
	footerLine: {
		marginTop: 24,
		fontSize: 13,
		color: "#71717a",
		textAlign: "center",
	},
}));

const ForgotPassword = () => {
	const classes = useStyles();
	const history = useHistory();
	const [email, setEmail] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [error, setError] = useState("");

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!email.trim()) return;
		setSubmitting(true);
		setError("");
		try {
			await openApi.post("/auth/password-reset/request", { email: email.trim() });
			setSubmitted(true);
		} catch (err) {
			// Backend may rate-limit (429) — surface that. Other errors are
			// hidden behind the generic "if it exists" copy.
			const status = err?.response?.status;
			if (status === 429) {
				setError(
					i18n.t("forgotPassword.errors.tooMany", {
						defaultValue: "Too many reset requests. Please wait an hour and try again.",
					})
				);
			} else {
				// Still show success — never leak existence of email.
				setSubmitted(true);
			}
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className={classes.root}>
			<div className={classes.card}>
				<div className={classes.brand}>
					<span className={classes.brandChip}><ChatRoundedIcon /></span>
					<span className={classes.brandText}>Whapiz</span>
				</div>

				<h1 className={classes.title}>
					{i18n.t("forgotPassword.title", { defaultValue: "Reset your password" })}
				</h1>
				<p className={classes.subtitle}>
					{i18n.t("forgotPassword.subtitle", {
						defaultValue: "Enter the email you signed up with. We'll send you a link to set a new password.",
					})}
				</p>

				{!submitted ? (
					<form onSubmit={handleSubmit} noValidate>
						<TextField
							variant="outlined"
							margin="normal"
							fullWidth
							required
							type="email"
							autoComplete="email"
							autoFocus
							label={i18n.t("forgotPassword.email", { defaultValue: "Email" })}
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							disabled={submitting}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<EmailIcon style={{ color: "#a1a1aa" }} />
									</InputAdornment>
								),
							}}
						/>
						{error ? (
							<p style={{ color: "#dc2626", fontSize: 13, margin: "8px 0 0" }}>{error}</p>
						) : null}
						<Button
							type="submit"
							fullWidth
							className={classes.submitBtn}
							disabled={submitting || !email.trim()}
						>
							{submitting
								? i18n.t("forgotPassword.sending", { defaultValue: "Sending…" })
								: i18n.t("forgotPassword.submit", { defaultValue: "Send reset link" })}
						</Button>
					</form>
				) : (
					<div className={classes.successBox}>
						{i18n.t("forgotPassword.success", {
							defaultValue: "If that account exists, we've sent a reset link to your inbox. Check your email (including spam).",
						})}
					</div>
				)}

				<div className={classes.footerLine}>
					<Link component={RouterLink} to="/login" style={{ color: "#0B0D0C", fontWeight: 600, display: "inline-block", padding: "12px 8px", minHeight: 44 }}>
						{i18n.t("forgotPassword.backToLogin", { defaultValue: "← Back to login" })}
					</Link>
				</div>
			</div>
		</div>
	);
};

export default ForgotPassword;
